import axios, { AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';

// If REACT_APP_API_URL is provided (e.g. in development) use it, otherwise default to
// an empty string so requests are sent to the same origin (works behind Nginx proxy).
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});
api.defaults.withCredentials = true;

export interface PageSummarySection<TData = unknown> {
  key: string;
  title: string;
  data: TData;
}

export interface PageSummaryPayload {
  page: string;
  viewer: {
    name: string | null;
    role: string | null;
  };
  generatedAt: string;
  personalisation?: {
    message: string | null;
  };
  sections: PageSummarySection[];
}

export interface DashboardSummaryPayload extends PageSummaryPayload {
  page: 'dashboard';
}

export interface PageSectionSummaryResult {
  sectionKey: string;
  title: string;
  summary: string;
  generatedAt: string;
}

export interface AuditFilters {
  page?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  userId?: number;
  serviceLine?: string;
  search?: string;
}

export interface AuraLensHealth {
  reachable: boolean;
  model: string;
  baseUrl: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface FormsStats {
  totalForms: number;
  activeForms: number;
  inactiveForms: number;
  optedOutForms: number;
  totalUsageGB: number;
}

// Simple JWT payload decoder (no verification)
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

let proactiveRefreshPromise: Promise<string | null> | null = null;

async function ensureTokenFresh(): Promise<string | null> {
  const token = useAuthStore.getState().token;
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return token;

  const expiresAt = payload.exp * 1000;
  const tenMinutes = 10 * 60 * 1000;
  if (Date.now() + tenMinutes < expiresAt) {
    // Token is still good for at least 10 minutes
    return token;
  }

  // Token is expiring soon — refresh proactively
  if (!proactiveRefreshPromise) {
    proactiveRefreshPromise = api
      .post('/api/auth/token/refresh')
      .then((res) => {
        const newToken = res.data?.access_token as string;
        if (newToken) {
          useAuthStore.getState().setToken(newToken);
        }
        return newToken || token;
      })
      .catch(() => token) // If proactive refresh fails, keep using current token; reactive refresh will handle 401s
      .finally(() => {
        proactiveRefreshPromise = null;
      });
  }
  return proactiveRefreshPromise;
}

// Request interceptor to add auth token and refresh proactively before expiry
api.interceptors.request.use(
  async (config) => {
    // Skip proactive refresh for auth endpoints to avoid loops
    const url = String(config.url || '');
    const isAuthEndpoint = url.includes('/auth/token') || url.includes('/auth/login');

    if (!isAuthEndpoint && !config.headers.Authorization) {
      const freshToken = await ensureTokenFresh();
      if (freshToken) {
        config.headers.Authorization = `Bearer ${freshToken}`;
      }
    } else if (!config.headers.Authorization) {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and silent refresh
let refreshPromise: Promise<string> | null = null;
const MAX_REFRESH_RETRIES = 2;

api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Reset idle timer on successful API activity so users aren't logged out while working
    try {
      useAuthStore.getState().resetIdleTimer();
    } catch {
      // ignore
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const requestUrl = String(originalRequest.url || '');

    // Report unexpected/serious API errors to Sentry so backend issues don't
    // stay hidden behind WhatsApp-only notifications.
    const status = error.response?.status;
    const isAuthEndpoint = requestUrl.includes('/auth/token') || 
                          requestUrl.includes('/auth/login') || 
                          requestUrl.includes('/auth/request-password-reset') ||
                          requestUrl.includes('/auth/reset-password') ||
                          requestUrl.includes('/auth/verify-email');
    const isUserDetailsEndpoint = requestUrl.includes('/auth/users/me');

    const shouldReport =
      status >= 500 ||
      error.message === 'Network Error' ||
      (status === 429 && !isAuthEndpoint);

    if (shouldReport) {
      Sentry.captureException(error, {
        level: status >= 500 ? 'error' : 'warning',
        tags: { source: 'api.response', status: String(status ?? 'none') },
        extra: {
          url: requestUrl,
          method: originalRequest.method,
          status,
          responseData: error.response?.data,
        },
      });
    }
    
    // Only attempt token refresh if:
    // 1. We got a 401 error
    // 2. User is already authenticated AND has user data (not during login)
    // 3. Not an auth endpoint
    // 4. Not already retrying
    // 5. Not the refresh endpoint itself
    const authState = useAuthStore.getState();
    const isAuthenticated = authState.isAuthenticated;
    const hasUserData = !!authState.user; // During login, user data isn't set yet
    const shouldAttemptRefresh = error.response?.status === 401 && 
                                 !originalRequest._retry && 
                                 !requestUrl.includes('/auth/token/refresh') && 
                                 !isAuthEndpoint &&
                                 isAuthenticated &&
                                 hasUserData; // Only refresh if user is fully logged in (has user data)
    
    if (shouldAttemptRefresh) {
      originalRequest._retry = true;
      let refreshAttempts = originalRequest._refreshAttempts || 0;

      try {
        if (!refreshPromise) {
          refreshPromise = (async () => {
            let lastErr: any;
            for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
              try {
                const res = await api.post('/api/auth/token/refresh');
                const newToken = res.data?.access_token as string;
                if (newToken) {
                  useAuthStore.getState().setToken(newToken);
                  return newToken;
                }
                throw new Error('No access_token in refresh response');
              } catch (err: any) {
                lastErr = err;
                // Retry on network errors only
                if (err.message === 'Network Error' && attempt < MAX_REFRESH_RETRIES - 1) {
                  await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
                  continue;
                }
                throw err;
              }
            }
            throw lastErr;
          })().finally(() => {
            refreshPromise = null;
          });
        }
        const token = await refreshPromise;
        if (token) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          originalRequest._refreshAttempts = refreshAttempts + 1;
          return api(originalRequest);
        }
        // If refresh succeeded but no token was returned, logout
        useAuthStore.getState().logout();
        toast.error('Session expired. Please login again.');
        return Promise.reject(error);
      } catch {
        // Refresh failed, logout user
        useAuthStore.getState().logout();
        toast.error('Session expired. Please login again.');
        return Promise.reject(error);
      }
    }
    
    // If we got a 401 but shouldn't refresh (e.g., during login), just reject
    if (error.response?.status === 401 && !isAuthEndpoint && !isAuthenticated) {
      // During login, don't show error toast - let the login handler deal with it
      return Promise.reject(error);
    }
    
    // Don't show generic error toasts for authentication endpoints or user details endpoint during login
    if (!isAuthEndpoint && !isUserDetailsEndpoint) {
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter || 60;
        toast.error(`Too many requests. Please wait ${retryAfter} seconds before trying again.`);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.');
      } else if (error.message === 'Network Error') {
        toast.error('Network error. Please check your connection.');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/api/auth/token', { 
      username: email, 
      password 
    });
    return response.data;
  },
  
  getCurrentUser: async (token?: string) => {
    const config: any = {};
    if (token) {
      // Ensure headers object exists and set Authorization
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    const response = await api.get('/api/auth/users/me', config);
    return response.data;
  },
  
  getAllUsers: async () => {
    const response = await api.get('/api/auth/users');
    return response.data;
  },
  
  createUser: async (email: string, username: string, isAdmin: boolean = false, role?: string, phoneNumber?: string) => {
    const response = await api.post('/api/auth/users', { 
      email, 
      username, 
      isAdmin,
      role,
      phoneNumber
    });
    return response.data;
  },
  
  forgotPassword: async (email: string) => {
    const response = await api.post('/api/auth/request-password-reset', { email });
    return response.data;
  },
  
  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/api/auth/reset-password', { 
      token, 
      newPassword 
    });
    return response.data;
  },
  
  verifyEmail: async (token: string, password: string) => {
    const response = await api.post('/api/auth/verify-email', { token, password });
    return response.data;
  },

  updateUser: async (id: number, data: { email?: string; username?: string; isAdmin?: boolean; role?: string; isActive?: boolean }) => {
    const response = await api.put(`/api/auth/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: number) => {
    const response = await api.delete(`/api/auth/users/${id}`);
    return response.data;
  },

  sendVerificationEmail: async (id: number) => {
    const response = await api.post(`/api/auth/users/${id}/send-verification`);
    return response.data;
  },

  sendOtp: async (otpToken: string) => {
    const response = await api.post('/api/auth/otp/send', { otp_token: otpToken });
    return response.data as { sent: boolean; message: string };
  },

  sendOtpEmail: async (otpToken: string) => {
    const response = await api.post('/api/auth/otp/send-email', { otp_token: otpToken });
    return response.data as { sent: boolean; message: string };
  },

  verifyOtp: async (otpToken: string, code: string) => {
    const response = await api.post('/api/auth/otp/verify', { otp_token: otpToken, code });
    return response.data as { access_token: string; token_type: string };
  },

  /** Opt-in, called right after a fresh OTP login if the user accepts the
   *  "Trust this device" prompt. Skips OTP on this browser for 30 days and
   *  revokes whichever device was previously trusted. Returns whether the
   *  confirmation email actually sent, so the caller can tell the user. */
  trustDevice: async () => {
    const response = await api.post('/api/auth/trust-device');
    return response.data as { message: string; email_sent: boolean };
  },

  savePhone: async (otpToken: string, phoneNumber: string) => {
    const response = await api.post('/api/auth/phone', { otp_token: otpToken, phone_number: phoneNumber });
    return response.data as { requires_otp: boolean; otp_token: string; message: string };
  },

  getLoginAudits: async (params?: { page?: number; limit?: number; action?: string; email?: string }) => {
    const response = await api.get('/api/auth/login-audits', { params });
    return response.data as {
      data: Array<{
        id: number;
        userId: number;
        email: string;
        action: string;
        ipAddress?: string;
        userAgent?: string;
        metadata?: Record<string, any>;
        createdAt: string;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    };
  },
};

// Forms API
export const formsAPI = {
  getFormsPage: async (
    params?: {
      search?: string;
      threshold?: number;
      view?: string;
      page?: number;
      limit?: number;
      customerName?: string;
      filter?: 'optedin' | 'optedout' | 'active' | 'inactive';
    }
  ) => {
    let url = '/api/forms';
    const qs: string[] = [];

    qs.push(`limit=${params?.limit ?? 100}`);
    qs.push(`page=${params?.page ?? 1}`);

    if (params?.search) qs.push(`search=${encodeURIComponent(params.search)}`);
    if (params?.threshold !== undefined) qs.push(`threshold=${params.threshold}`);
    if (params?.view) qs.push(`view=${encodeURIComponent(params.view)}`);
    if (params?.customerName) qs.push(`customerName=${encodeURIComponent(params.customerName)}`);
    if (params?.filter) qs.push(`filter=${encodeURIComponent(params.filter)}`);

    url += `?${qs.join('&')}`;
    const response = await api.get(url);
    return response.data as PaginatedResponse<any> & { stats?: FormsStats };
  },

  getAllForms: async (params?: { search?: string; threshold?: number; view?: string }) => {
    let url = '/api/forms';
    const qs: string[] = [];
    
    // Request a very large batch so the Pool Customers page can load in one
    // round-trip for typical datasets and avoid slow sequential page walking.
    qs.push('limit=5000');
    qs.push('page=1');
    
    if (params?.search) qs.push(`search=${encodeURIComponent(params.search)}`);
    if (params?.threshold !== undefined) qs.push(`threshold=${params.threshold}`);
    if (params?.view) qs.push(`view=${encodeURIComponent(params.view)}`);
    
    url += `?${qs.join('&')}`;
    const response = await api.get(url);
    
    // Handle paginated response - fetch any remaining pages in parallel and
    // degrade gracefully if one page fails instead of failing the whole screen.
    if (response.data && response.data.pagination && response.data.pagination.hasNext) {
      const allForms = [...(response.data.data || [])];
      const currentPage = response.data.pagination.page || 1;
      const totalPages = response.data.pagination.totalPages || 1;

      const remainingPageRequests: Promise<any>[] = [];
      for (let page = currentPage + 1; page <= totalPages; page++) {
        const pageQs = [...qs];
        const pageIndex = pageQs.findIndex(q => q.startsWith('page='));
        if (pageIndex >= 0) {
          pageQs[pageIndex] = `page=${page}`;
        } else {
          pageQs.push(`page=${page}`);
        }

        remainingPageRequests.push(api.get(`/api/forms?${pageQs.join('&')}`));
      }

      const remainingPages = await Promise.allSettled(remainingPageRequests);
      for (const result of remainingPages) {
        if (result.status === 'fulfilled' && result.value?.data?.data) {
          allForms.push(...result.value.data.data);
        } else if (result.status === 'rejected') {
          console.warn('Failed to fetch a forms page; continuing with partial results.', result.reason);
        }
      }
      
      // Return in the same structure as the API response
      return { 
        data: allForms, 
        pagination: { 
          page: 1,
          limit: allForms.length,
          total: response.data.pagination.total || allForms.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false
        } 
      };
    }
    
    // If no pagination or single page, return as-is (but ensure structure is consistent)
    if (response.data && response.data.data) {
      return response.data;
    }
    
    // Fallback: if response is already an array (shouldn't happen with current API)
    if (Array.isArray(response.data)) {
      return { data: response.data, pagination: { page: 1, limit: response.data.length, total: response.data.length, totalPages: 1, hasNext: false, hasPrev: false } };
    }
    
    return response.data;
  },
  
  getForm: async (id: string) => {
    const response = await api.get(`/api/forms/${id}`);
    return response.data;
  },

  getWorkspace: async (id: string) => {
    const response = await api.get(`/api/forms/${id}/workspace`);
    return response.data;
  },

  addWorkspaceNote: async (id: string, payload: { body: string; kind?: string }) => {
    const response = await api.post(`/api/forms/${id}/workspace/notes`, payload);
    return response.data;
  },
  
  createForm: async (formData: any) => {
    const response = await api.post('/api/forms', formData);
    return response.data;
  },

  previewBulkImport: async (file: File, formType: 'transfer' | 'regular' = 'transfer') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('formType', formType);
    const response = await api.post('/api/forms/bulk-import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },

  commitBulkImport: async (rows: any[], formType: 'transfer' | 'regular' = 'transfer') => {
    const response = await api.post('/api/forms/bulk-import/commit', { rows, formType }, {
      timeout: 120000,
    });
    return response.data;
  },

  previewBulkEdit: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/forms/bulk-edit/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },

  commitBulkEdit: async (rows: any[]) => {
    const response = await api.post('/api/forms/bulk-edit/commit', { rows }, {
      timeout: 120000,
    });
    return response.data;
  },
  
  updateForm: async (id: string, formData: any) => {
    const putWithTimeout = () => api.put(`/api/forms/${id}`, formData, { timeout: 30000 });
    try {
      const response = await putWithTimeout();
      return response.data;
    } catch (error: any) {
      // Retry once on timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const retryResponse = await putWithTimeout();
        return retryResponse.data;
      }
      // If it's a 429 error, wait and retry once
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter || 60;
        
        // Wait for the specified time
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        
        // Retry once
        const retryResponse = await putWithTimeout();
        return retryResponse.data;
      }
      throw error;
    }
  },
  
  deleteForm: async (id: string) => {
    const response = await api.delete(`/api/forms/${id}`);
    return response.data;
  },
  
  getFormAudit: async (id: string) => {
    const response = await api.get(`/api/forms/${id}/audit`);
    return response.data;
  },

  resetTopup: async () => {
    const response = await api.post('/api/forms/reset-topup');
    return response.data;
  },
  
  getAllAudits: async (filters?: AuditFilters) => {
    const response = await api.get('/api/forms/audit', { params: filters });
    return response.data;
  },

  // Export requires dateFrom/dateTo (enforced server-side too) so a query
  // can't run totally unscoped, but the backend streams the file as it's
  // generated with no row cap — so no client-side timeout either, since a
  // very wide date range can legitimately take a while to fully download.
  exportAudit: async (filters: AuditFilters & { dateFrom: string; dateTo: string }) => {
    const response = await api.get('/api/forms/audit/export', {
      params: filters,
      responseType: 'blob',
      timeout: 0,
    });
    return response.data;
  },
  
  exportForms: async (filters?: { search?: string; filterOption?: string; role?: string }) => {
    let url = '/api/forms/export';
    const qs: string[] = [];
    if (filters?.search) qs.push(`search=${encodeURIComponent(filters.search)}`);
    if (filters?.filterOption) qs.push(`filter=${encodeURIComponent(filters.filterOption)}`);
    if (filters?.role) qs.push(`role=${encodeURIComponent(filters.role)}`);
    if (qs.length) url += `?${qs.join('&')}`;
    
    const response = await api.get(url, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  // Paginated export to avoid rate limits
  exportFormsPaginated: async (page: number = 1, limit: number = 100) => {
    const response = await api.get(`/api/forms/export/paginated?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Export all service lines (for Service Forms page)
  exportAllServiceLines: async (filters?: { search?: string; filterOption?: string }) => {
    let url = '/api/forms/export/all-service-lines';
    const qs: string[] = [];
    if (filters?.search) qs.push(`search=${encodeURIComponent(filters.search)}`);
    if (filters?.filterOption) qs.push(`filter=${encodeURIComponent(filters.filterOption)}`);
    if (qs.length) url += `?${qs.join('&')}`;
    
    const response = await api.get(url, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Debug endpoint to check rate limit status
  getRateLimitStatus: async () => {
    const response = await api.get('/api/forms/rate-limit-status');
    return response.data;
  },

  // Transfer data between two service forms
  transferData: async (sourceFormId: number, destinationFormId: number, transferAmount: number) => {
    const response = await api.post('/api/forms/transfer', {
      sourceFormId,
      destinationFormId,
      transferAmount
    });
    return response.data;
  },

  // Deactivate service line via Starlink API
  deactivateServiceLine: async (id: number, endNow: boolean = true, accountNumber?: string) => {
    const response = await api.post(`/api/forms/${id}/deactivate`, {
      endNow,
      accountNumber
    });
    return response.data;
  },

  // Reactivate/Resume service line via Starlink API (productReferenceId is fetched automatically)
  reactivateServiceLine: async (id: number, accountNumber?: string) => {
    const response = await api.post(`/api/forms/${id}/reactivate`, {
      accountNumber
    });
    return response.data;
  },
};

// Usage 100% API
export const usage100PercentAPI = {
  // Get all 100% usage forms
  getAll: async () => {
    const response = await api.get('/api/usage-100-percent');
    return response.data;
  },

  // Get statistics
  getStatistics: async () => {
    const response = await api.get('/api/usage-100-percent/statistics');
    return response.data;
  },

  // Update notes for a record
  updateNotes: async (id: number, notes: string) => {
    const response = await api.put(`/api/usage-100-percent/${id}/notes`, { notes });
    return response.data;
  },

  // Deactivate a record (Admin only)
  deactivate: async (id: number) => {
    const response = await api.put(`/api/usage-100-percent/${id}/deactivate`);
    return response.data;
  },

  // Manually check and track a form
  checkForm: async (formId: number) => {
    const response = await api.post(`/api/usage-100-percent/check/${formId}`);
    return response.data;
  },

  // Export all 100% usage forms
  exportAll: async () => {
    const response = await api.get('/api/usage-100-percent/export', {
      responseType: 'blob'
    });
    return response.data;
  },
};

// Monitor API
export const monitorAPI = {
  getStatus: async () => {
    const response = await api.get('/api/monitor/control/status');
    return response.data;
  },

  getCommandCenter: async () => {
    const response = await api.get('/api/monitor/command-center');
    return response.data;
  },

  getPageSummaryPayload: async (pageKey: string) => {
    const response = await api.get(`/api/monitor/page-summary/${pageKey}`);
    return response.data as PageSummaryPayload;
  },

  getAuraLensHealth: async () => {
    const response = await api.get('/api/monitor/page-summary/health');
    return response.data as AuraLensHealth;
  },

  generatePageSectionSummary: async (page: string, sectionKey: string, payload: PageSummaryPayload) => {
    const response = await api.post('/api/monitor/page-summary/generate', {
      page,
      sectionKey,
      payload,
    });
    return response.data as PageSectionSummaryResult;
  },
  
  getLogs: async (maxLines = 100) => {
    const response = await api.get(`/api/monitor/logs?max_lines=${maxLines}`);
    return response.data;
  },
  
  startMonitor: async () => {
    const response = await api.post('/api/monitor/control/start');
    return response.data;
  },
  
  stopMonitor: async () => {
    const response = await api.post('/api/monitor/control/stop');
    return response.data;
  },

  downloadLogs: async (maxLines = 100) => {
    const response = await api.get(`/api/monitor/logs/download?max_lines=${maxLines}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Config endpoints
  getNotificationEmail: async () => {
    const res = await api.get('/api/monitor/config/email');
    return res.data.email as string | null;
  },

  setNotificationEmail: async (email: string | null) => {
    const res = await api.post('/api/monitor/config/email', { email });
    return res.data.email as string | null;
  },

  getAlertRecipients: async () => {
    const res = await api.get('/api/monitor/config/alert-recipients');
    return res.data as Array<{
      userId: number;
      username: string;
      email: string;
      role: string;
      hasPhoneNumber: boolean;
      notifyEmail: boolean;
      notifyWhatsapp: boolean;
    }>;
  },

  updateAlertRecipient: async (userId: number, updates: { notifyEmail?: boolean; notifyWhatsapp?: boolean }) => {
    const res = await api.post('/api/monitor/config/alert-recipients', { userId, ...updates });
    return res.data as { userId: number; notifyEmail: boolean; notifyWhatsapp: boolean };
  },

  getAuditLog: async () => {
    const res = await api.get('/api/monitor/control/audit-log');
    return res.data as Array<{
      id: number;
      action: 'start' | 'stop';
      source: 'manual' | 'auto';
      userId: number | null;
      email: string | null;
      ipAddress: string | null;
      metadata: Record<string, any> | null;
      createdAt: string;
    }>;
  },

  // Manual trigger for testing
  triggerMonitor: async () => {
    const res = await api.post('/api/monitor/control/trigger');
    return res.data;
  },
  getCurrentMonthTotal: async () => {
    const res = await api.get('/api/monitor/usage/aggregate/current');
    return res.data as { year: number; month: number; totalPriorityDataGB: number; computedAt: string | null; totalServiceLines: number; emailStatus: 'sent' | 'pending' };
  },
  recomputeCurrentMonthTotal: async () => {
    const res = await api.post('/api/monitor/usage/aggregate/recompute');
    return res.data as { message: string; year: number; month: number; totalPriorityDataGB: number; computedAt: string };
  },
  getUsageHistory: async (limit = 12) => {
    const res = await api.get(`/api/monitor/usage/aggregate/history?limit=${limit}`);
    return res.data as Array<{ id: number; year: number; month: number; totalPriorityDataGB: number; computedAt: string; totalServiceLines: number; emailStatus: 'sent' | 'pending' }>;
  },
  getSummaryCards: async () => (await api.get('/api/monitor/usage/aggregate/summary')).data as {
    totalUsageGB: number;
    totalCustomers: number;
    currentMonthUsageGB: number;
  },
  exportDataUsageReport: async (account: 'main' | 'datapool' | 'impactplan') => {
    const response = await api.get(`/api/monitor/usage/export/${account}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data as Blob;
  },
  exportDataTableReport: async (
    account: 'main' | 'datapool' | 'impactplan',
    report: 'service-lines' | 'kits' | 'addresses' | 'routers' | 'service-line-kits'
  ) => {
    const response = await api.get(`/api/monitor/usage/export/${account}/${report}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data as Blob;
  },
  exportCustomDataUsageReport: async (payload: {
    accounts: Array<'main' | 'datapool' | 'impactplan'>;
    startMonth: string;
    endMonth: string;
    columns: Array<'serviceLineNumber' | 'nickname' | 'productReferenceId' | 'kitSerialNumber' | 'active' | 'startDate' | 'endDate' | 'packageLimit'>;
  }) => {
    const response = await api.post('/api/monitor/usage/export/custom', payload, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data as Blob;
  },
  exportDataPoolMonthlyReport: async (payload: {
    startMonth: string;
    endMonth: string;
    columns: Array<'serviceLineNumber' | 'nickname' | 'productReferenceId' | 'kitSerialNumber' | 'active' | 'startDate' | 'endDate' | 'packageLimit'>;
  }) => {
    const response = await api.post('/api/monitor/usage/export/datapool/monthly', payload, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data as Blob;
  },
  getAccountId: async () => (await api.get('/api/monitor/config/account-id')).data.accountId as string | null,
  startRangeAggregation: async (payload: { accountId?: string; startYear: number; startMonth: number; endYear?: number; endMonth?: number }) => {
    const res = await api.post('/api/monitor/usage/aggregate/range', payload);
    return res.data as { jobId: string };
  },
  getRangeAggregationProgress: async (jobId: string) => (await api.get(`/api/monitor/usage/aggregate/range/${jobId}/progress`)).data as {
    status: 'running' | 'done' | 'error';
    message?: string;
    currentIndex: number;
    totalMonths: number;
    completedBatches: number;
    totalBatches: number;
    phase1: number;
    phase2: number;
  },
};

// Generic API helper
export const apiRequest = async (
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  endpoint: string,
  data?: any
) => {
  try {
    const response = await api.request({
      method,
      url: endpoint,
      data,
    });
    return response.data;
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: { source: 'apiRequest' },
      extra: { method, endpoint, data },
    });
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const packageAPI = {
  list: async () => (await api.get('/api/packages')).data,
  create: async (capGB: number) => (await api.post('/api/packages', { capGB })).data,
  remove: async (id: number) => (await api.delete(`/api/packages/${id}`)).data,
};

export interface MigrationUploadRow {
  order: number;
  serviceLineNumber: string;
  deviceId: string;
  kitNumber?: string;
  productReferenceId?: string;
  productId?: string;
  endNow?: boolean | string;
  reasonForCancellation?: string;
}

export type MigrationAccount = 'datapool' | 'main' | 'impactplan';
export type MigrationStepKey = 'deactivate' | 'detach' | 'removeFromSource' | 'addToTarget' | 'resumeService' | 'attachAndResume' | 'optInPriorityData';

export interface MigrationJobProgress {
  id: string;
  status: 'running' | 'done' | 'error';
  step: MigrationStepKey;
  stepLabel: string;
  account: MigrationAccount;
  ownerEmail: string;
  totalRows: number;
  processedRows: number;
  successfulRows: number;
  failedRows: number;
  batchSize: number;
  currentBatch: number;
  totalBatches: number;
  percentComplete: number;
  logs: string[];
  createdAt: string;
  updatedAt: string;
  rows: Array<{
    order: number;
    serviceLineNumber: string;
    deviceId: string;
    productReferenceId: string;
    status: 'pending' | 'running' | 'success' | 'failed';
    message?: string;
    steps: Array<{
      key: MigrationStepKey;
      label: string;
      status: 'pending' | 'running' | 'success' | 'failed';
      message?: string;
    }>;
  }>;
}

export interface MigrationHistoryFile {
  id: string;
  dateKey: string;
  dateLabel: string;
  step: MigrationStepKey;
  stageLabel: string;
  account: MigrationAccount;
  accountLabel: string;
  filename: string;
  createdAt: string;
  totalRows: number;
  successfulRows: number;
  failedRows: number;
}

export interface MigrationHistoryDate {
  dateKey: string;
  dateLabel: string;
  totalFiles: number;
  stages: Array<{
    step: MigrationStepKey;
    stageLabel: string;
    files: MigrationHistoryFile[];
  }>;
}

export const migrationsAPI = {
  start: async (payload: { step: MigrationStepKey; account: MigrationAccount; rows: MigrationUploadRow[] }) => {
    const response = await api.post('/api/migrations/jobs', payload);
    return response.data as { jobId: string };
  },
  getJob: async (jobId: string) => {
    const response = await api.get(`/api/migrations/jobs/${encodeURIComponent(jobId)}`);
    return response.data as MigrationJobProgress;
  },
  listJobs: async () => {
    const response = await api.get('/api/migrations/jobs');
    return response.data as { jobs: MigrationJobProgress[] };
  },
  getHistory: async () => {
    const response = await api.get('/api/migrations/history');
    return response.data as { dates: MigrationHistoryDate[] };
  },
  downloadHistoryFile: async (dateKey: string, filename: string) => {
    const response = await api.get(`/api/migrations/history/${encodeURIComponent(dateKey)}/files/${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  downloadHistoryDateZip: async (dateKey: string) => {
    const response = await api.get(`/api/migrations/history/${encodeURIComponent(dateKey)}/download-all`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

// Power BI API
export const powerbiAPI = {
  getStatus: async () => {
    const response = await api.get('/api/powerbi/status');
    return response.data;
  },
  
  start: async () => {
    const response = await api.post('/api/powerbi/start');
    return response.data;
  },
  
  stop: async () => {
    const response = await api.post('/api/powerbi/stop');
    return response.data;
  },
  
  run: async () => {
    const response = await api.post('/api/powerbi/run');
    return response.data;
  },
  
  getLogs: async (maxLines: number = 100) => {
    const response = await api.get(`/api/powerbi/logs?max_lines=${maxLines}`);
    return response.data;
  },
  
  getTables: async () => {
    const response = await api.get('/api/powerbi/data/tables');
    return response.data;
  },
  
  getTableData: async (tableName: string, limit: number = 10) => {
    const response = await api.get(`/api/powerbi/data/${tableName}?limit=${limit}`);
    return response.data;
  },
  
  exportTable: async (tableName: string) => {
    const response = await api.get(`/api/powerbi/data/export/${tableName}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

// Xero API
export const xeroAPI = {
  getStatus: async () => {
    const response = await api.get('/api/xero/status');
    return response.data;
  },
  
  start: async () => {
    const response = await api.post('/api/xero/start');
    return response.data;
  },
  
  stop: async () => {
    const response = await api.post('/api/xero/stop');
    return response.data;
  },
  
  run: async () => {
    const response = await api.post('/api/xero/run');
    return response.data;
  },
  
  getLogs: async (maxLines: number = 100) => {
    const response = await api.get(`/api/xero/logs?max_lines=${maxLines}`);
    return response.data;
  },
  
  getTables: async () => {
    const response = await api.get('/api/xero/data/tables');
    return response.data;
  },
  
  getTableData: async (tableName: string, limit: number = 10) => {
    const response = await api.get(`/api/xero/data/${tableName}?limit=${limit}`);
    return response.data;
  },
  
  exportTable: async (tableName: string) => {
    const response = await api.get(`/api/xero/data/export/${tableName}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

// Impact Plan API
export const impactplanAPI = {
  getStatus: async () => {
    const response = await api.get('/api/impactplan/control/status');
    return response.data;
  },
  
  getLogs: async (maxLines = 100) => {
    const response = await api.get(`/api/impactplan/logs?max_lines=${maxLines}`);
    return response.data;
  },
  
  startMonitor: async () => {
    const response = await api.post('/api/impactplan/control/start');
    return response.data;
  },
  
  stopMonitor: async () => {
    const response = await api.post('/api/impactplan/control/stop');
    return response.data;
  },
  
  downloadLogs: async (maxLines = 100) => {
    const response = await api.get(`/api/impactplan/logs/download?max_lines=${maxLines}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Manual trigger for testing
  triggerMonitor: async () => {
    const res = await api.post('/api/impactplan/control/trigger');
    return res.data;
  },

  // Get all impact plan accounts
  getAccounts: async (search?: string) => {
    let url = '/api/impactplan/accounts';
    if (search) {
      url += `?search=${encodeURIComponent(search)}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  // Get Impact Plan forms (formatted like service forms) - fetch all forms
  getForms: async () => {
    const response = await api.get('/api/impactplan/forms');
    return response.data;
  },

  // Export Impact Plan forms to Excel
  exportForms: async (filters?: { search?: string; filterOption?: string }) => {
    let url = '/api/impactplan/forms/export';
    const qs: string[] = [];
    if (filters?.search) qs.push(`search=${encodeURIComponent(filters.search)}`);
    if (filters?.filterOption) qs.push(`filterOption=${encodeURIComponent(filters.filterOption)}`);
    if (qs.length) url += `?${qs.join('&')}`;
    
    const response = await api.get(url, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Create or update customer mapping
  saveCustomer: async (serviceLineNumber: string, customerName: string) => {
    const response = await api.post('/api/impactplan/customer', {
      service_line_number: serviceLineNumber,
      customer_name: customerName
    });
    return response.data;
  },

  // Get customer mapping for a service line
  getCustomer: async (serviceLineNumber: string) => {
    const response = await api.get(`/api/impactplan/customer/${encodeURIComponent(serviceLineNumber)}`);
    return response.data;
  },

  // Delete customer mapping
  deleteCustomer: async (serviceLineNumber: string) => {
    const response = await api.delete(`/api/impactplan/customer/${encodeURIComponent(serviceLineNumber)}`);
    return response.data;
  },
};

// Impact Plan 100% Usage API
export const impactplan100PercentAPI = {
  // Get all Impact Plan 100% usage forms
  getAll: async () => {
    const response = await api.get('/api/impactplan-100-percent');
    return response.data;
  },

  // Get statistics
  getStatistics: async () => {
    const response = await api.get('/api/impactplan-100-percent/statistics');
    return response.data;
  },

  // Update notes for a record
  updateNotes: async (id: number, notes: string) => {
    const response = await api.put(`/api/impactplan-100-percent/${id}/notes`, { notes });
    return response.data;
  },

  // Deactivate a record (Admin only)
  deactivate: async (id: number) => {
    const response = await api.put(`/api/impactplan-100-percent/${id}/deactivate`);
    return response.data;
  },

  // Export to Excel
  export: async () => {
    const response = await api.get('/api/impactplan-100-percent/export', {
      responseType: 'blob'
    });
    return response.data;
  },
};

// Email Preferences API
export const emailPreferencesAPI = {
  // Get all users with their email preferences
  getAllUsersWithPreferences: async (): Promise<any[]> => {
    const response = await api.get('/api/email-preferences');
    return response.data;
  },
  
  // Get email preferences for a specific user
  getUserPreferences: async (userId: number): Promise<any> => {
    const response = await api.get(`/api/email-preferences/${userId}`);
    return response.data;
  },
  
  // Update email preferences for a user
  updateUserPreferences: async (userId: number, preferences: any): Promise<any> => {
    const response = await api.put(`/api/email-preferences/${userId}`, preferences);
    return response.data;
  },
};

export const notificationsAPI = {
  list: async (params?: { unread?: boolean; severity?: string; source?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.unread) searchParams.set('unread', 'true');
    if (params?.severity) searchParams.set('severity', params.severity);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await api.get(`/api/notifications${suffix}`);
    return response.data as {
      data: Array<{
        id: number;
        type: string;
        severity: 'info' | 'warning' | 'error' | 'critical' | 'success';
        title: string;
        body: string;
        source?: string;
        targetUrl?: string;
        actionLabel?: string;
        readAt?: string | null;
        createdAt: string;
        metadata?: Record<string, unknown>;
      }>;
      unreadCount: number;
    };
  },
  markRead: async (id: number) => {
    const response = await api.post(`/api/notifications/${id}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await api.post('/api/notifications/read-all');
    return response.data;
  }
};

export interface UsageEventRow {
  id: number;
  eventId: string;
  event: string;
  serviceLine: string;
  payload: Record<string, any>;
  status: 'pending' | 'in_flight' | 'delivered' | 'failed' | 'expired';
  attempts: number;
  availableAt: string;
  lockedUntil?: string | null;
  claimedBy?: string | null;
  deliveredAt?: string | null;
  lastError?: string | null;
  deliveryStatus?: Partial<Record<'email' | 'whatsapp', {
    status: 'queued' | 'sent' | 'failed' | 'skipped';
    providerMessageId?: string | null;
    recipient?: string | null;
    detail?: string | null;
    updatedAt: string;
  }>>;
  createdAt: string;
  updatedAt: string;
}

export const usageEventsAPI = {
  list: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    event?: string;
    serviceLine?: string;
    deliveryChannel?: 'email' | 'whatsapp';
    deliveryStatus?: 'queued' | 'sent' | 'failed' | 'skipped';
  }) => {
    const response = await api.get('/api/admin/usage-events', { params });
    return response.data as {
      data: UsageEventRow[];
      pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
    };
  },
  stats: async () => {
    const response = await api.get('/api/admin/usage-events/stats');
    return response.data as {
      enabled: boolean;
      counts: Record<'pending' | 'in_flight' | 'delivered' | 'failed' | 'expired', number>;
      oldestPendingAt: string | null;
    };
  },
  requeue: async (eventId: string) => {
    const response = await api.post(`/api/admin/usage-events/${eventId}/requeue`);
    return response.data as { status: 'requeued' };
  },
};

export default api; 
